<template>
    <custom-table
        :title="$t('pages.ecommerce.productManagement.category.title')"
        :subTitle="$t('pages.ecommerce.productManagement.category.subTitle')"
        expandableRows
        selectableRows
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        :columns="fields"
        :actions="actions"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow">
        <template v-slot:title="{ row: record }">
            {{ resolveDatum(record.translations, $root.defaultLanguage.id, 'language_id').title }}
        </template>
        <template v-slot:status="{ row: record }">
            <span :class="record.active ? 'badge-light-success': 'badge-light-danger'" class="badge">{{ record.active ? $t('common.active') : $t('common.passive') }}</span>
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <a v-on:click="newCategory(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/arrows/arr075.svg" />
                    </span>
                </a>
                <a v-on:click="fetchCategory(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/art/art005.svg" />
                    </span>
                </a>
                <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord([record.id])">
                    <template #reference>
                        <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                            </span>
                        </a>
                    </template>
                </el-popconfirm>
            </div>
        </template>
    </custom-table>
    <div class="modal fade" id="kt_modal_add_category" ref="addCategoryModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ form.title }}</h2>
                    <div id="kt_modal_add_customer_close" data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onSubmit()" :model="form" ref="categoryForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div class="scroll-y me-n7 pe-7" id="kt_modal_add_customer_scroll" data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto" data-kt-scroll-dependencies="#kt_modal_add_customer_header" data-kt-scroll-wrappers="#kt_modal_add_customer_scroll" data-kt-scroll-offset="300px">
                            <el-divider content-position="center">
                                {{ $t("pages.ecommerce.productManagement.category.generalInformation") }}
                            </el-divider>

                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.cols.title') }}</label>
                                <el-form-item prop="translate.title" :rules="$validation.getMessage(['required'])">
                                    <el-input v-model="form.translate.title" type="text"/>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.cols.description') }}</label>
                                <el-form-item prop="translate.description">
                                    <!--<el-input v-model="form.translate.description" type="textarea" rows="5"/>-->
                                    <CustomCkeditor v-if="form.visible" :model="form.translate.description" @updateModel="form.translate.description = $event"></CustomCkeditor>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.cols.icon') }}</label>
                                <el-form-item prop="data.icon">
                                    <el-input v-model="form.data.icon" type="text"/>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.cols.sort') }}</label>
                                <el-form-item prop="data.sort" :rules="$validation.getMessage(['required'])">
                                    <el-input-number v-model="form.data.sort" :min="1" />
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-10">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.cols.status') }}</label>
                                <el-form-item prop="data.active">
                                    <el-radio-group v-model="form.data.active">
                                        <el-radio-button :label="true">{{ $t("common.active") }}</el-radio-button>
                                        <el-radio-button :label="false">{{ $t("common.passive") }}</el-radio-button>
                                    </el-radio-group>
                                </el-form-item>
                            </div>

                            <el-divider content-position="center">
                                {{ $t("pages.ecommerce.productManagement.category.seoSettings") }}
                            </el-divider>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.seoCols.slug') }}</label>
                                <el-form-item prop="translate.meta.slug">
                                    <el-input v-model="form.translate.slug" type="text"/>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.seoCols.title') }}</label>
                                <el-form-item prop="translate.meta.title">
                                    <el-input v-model="form.translate.meta.title" type="text"/>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.seoCols.description') }}</label>
                                <el-form-item prop="translate.meta.description">
                                    <el-input v-model="form.translate.meta.description" type="textarea" rows="3"/>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.productManagement.category.seoCols.keywords') }}</label>
                                <el-form-item prop="translate.meta.keyword">
                                    <el-input v-model="form.translate.meta.keyword" type="textarea" rows="3"/>
                                </el-form-item>
                            </div>

                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";
import CustomCkeditor from "@/components/custom-ckeditor";

export default {
    name: "index",
    components: {
        CustomTable,
        CustomCkeditor,
    },
    data(){
        return {
            table: {
                loading: false,
                data: []
            },
            fields: [
                {
                    name: this.$t("pages.ecommerce.productManagement.category.cols.id"),
                    key: "id",
                    sortable: true
                },
                {
                    name: this.$t("pages.ecommerce.productManagement.category.cols.title"),
                    scopedSlots: {customRender: "title"}
                },
                {
                    name: this.$t("pages.ecommerce.productManagement.category.cols.sort"),
                    key: "sort"
                },
                {
                    name: this.$t("pages.ecommerce.productManagement.category.cols.status"),
                    scopedSlots: {customRender: "status"}
                },
                {
                    name: this.$t("common.action"),
                    key: "action",
                    scopedSlots: {customRender: "actions"}
                }
            ],
            actions: [
                {
                    name: "new",
                    icon: "bi-plus-lg",
                    label: "btn.add"
                },
                {
                    name: "refresh",
                    icon: "bi-arrow-repeat",
                    label: "btn.refresh"
                },
                {
                    name: "delete",
                    icon: "bi-trash",
                    label: "btn.delete"
                }
            ],
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                visible: false,
                translate: {
                    language_id: this.$root.defaultLanguage.id,
                    meta: {}
                },
                data: {
                    sort: 1,
                    active: true
                }
            },
            selectedRowKeys: []
        }
    },
    mounted(){
        this.setCurrentPageBreadcrumbs(this.$t("menu.categories"), [this.$t("menu.productManagement")]);
        this.loadTable();
    },
    methods: {
        newCategory(record = null){
            this.form.updateStatus = false;
            this.form.translate = {
                language_id: this.$root.defaultLanguage.id,
                meta: {}
            };
            this.form.data = {
                sort: 1,
                parent_id: null,
                active: true
            };
            this.form.title = this.$t("pages.ecommerce.productManagement.category.newCategory");

            if(record) {
                let categoryTranslate = this.resolveDatum(record.translations, this.$root.defaultLanguage.id, 'language_id');

                this.form.title = this.sprintf(this.$t("pages.ecommerce.productManagement.category.newCategoryWithParent"), [categoryTranslate.title]);
                this.form.data.parent_id = record.id;
            }

            this.showModal(this.$refs.addCategoryModal);
            this.form.visible = true;
        },
        fetchCategory(record){
            this.form.updateStatus = true;
            this.axios.get(this.endpoints['ecommerce_product_category']+ '/' + record.id).then(response => {
                let data = response.data.data;

                let translate = this.resolveDatum(data.translations, this.$root.defaultLanguage.id, 'language_id');
                let translateLinks = this.resolveDatum(data.links, this.$root.defaultLanguage.id, 'language_id');

                this.form.title =  this.sprintf(this.$t("pages.ecommerce.productManagement.category.editCategory"), [translate.title]);
                this.form.translate = Object.assign(translate, {
                    meta: {
                        title: translateLinks.title,
                        description: translateLinks.description,
                        keyword: translateLinks.keyword
                    }
                });
                this.form.data = response.data.data;

                this.showModal(this.$refs.addCategoryModal);
                this.form.visible = true;
            });
        },
        onSubmit(){
            this.$refs.categoryForm.validate((valid) => {
                if(valid) {
                    let formData = this.prepareCategoryFormData();
                    this.form.loading = true;

                    if(this.form.data.id) {
                        this.axios.put(this.endpoints['ecommerce_product_category'] + '/' + this.form.data.id, formData).then(response => {
                            this.onResponse(response.data, () => {
                                this.loadTable();
                                this.hideModal(this.$refs.addCategoryModal);
                                this.form.visible = false;
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }else {
                        this.axios.post(this.endpoints['ecommerce_product_category'], formData).then(response => {
                            this.onResponse(response.data, () => {
                                this.loadTable();
                                this.hideModal(this.$refs.addCategoryModal);
                                this.form.visible = false;
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }
                } else {
                    return false;
                }
            });
        },
        prepareCategoryFormData(){
            let formData = this.form.data;

            return {...this.form.translate, ...formData};
        },
        deleteRecord(id){
            this.$store.dispatch("ecommerce/productManagement/product/category/delete", {
                id: id
            }).then((successDeleted) => {
                this.selectedRowKeys = this.selectedRowKeys.filter(x => !successDeleted.includes(x));
                if(successDeleted) {
                    this.loadTable();
                }
            });
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.loadTable();
                    break;

                case "new":
                    this.newCategory();
                    break;

                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;

                default:
                    break;
            }
        },
        loadTable(){
            this.table.loading = true;

            this.axios.get(this.endpoints['ecommerce_product_category'] + '/tree').then((response) => {
                this.table.data = response.data.data;
            }).finally(() => {
                this.table.loading = false;
            });
        },
        handleSelectedRow(record){
            this.selectedRowKeys = record;
        }
    }
}
</script>

<style>
</style>